import Drawer from '@material-ui/core/Drawer'
import Link from '@material-ui/core/Link'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React from 'react'
import { NavLink } from 'react-router-dom'
import { useMediaQueryDown } from '../../../../hooks/useMediaQuery'
import { useTrans } from '../../../../services/i18n'
import Typography from '../../../common/Typography'
import * as MENU_ICONS from '../../../svg/Icons/Menu'
import { IconDashboard } from '../../../svg/Icons/Menu'
import { ColoredLogoSvg } from '../../../svg/logos'
import { IconMenuControl } from '../../../svg'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import useStyles from './styles'
import { Collapse } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import { useAppMenu } from './hooks/useActiveGroup'
import IconButton from '@material-ui/core/IconButton'
import connect from './connect'
import { useSelector } from 'react-redux'
import {
  selectUserPermissions,
  isEnabled,
  selectUserRole,
} from '../../../../selectors'

const activeLinkStyle = isActive => ({
  display: 'flex',
  alignItems: 'center',
  padding: '2px 10px',
  ...(isActive
    ? { color: '#f32445', borderLeft: `2px solid #f32445` }
    : {}),
})

const standaloneActiveStyle = {
  color: '#f32445',
  borderLeft: `2px solid #f32445`,
}

export const AppMenu = ({
  setDrawerOpen,
  openDrawer,
  setDrawerClose,
  authUser,
}) => {
  const classes = useStyles()
  const { trans } = useTrans()
  const isSmallScreen = useMediaQueryDown('sm')

  const { menu, openGroupKey, toggleGroup, activeGroupKey } = useAppMenu()

  const permissions = useSelector(selectUserPermissions)
  const userRole = useSelector(selectUserRole)

  const isEnabledRoute = route => {
    // Match route gating: top-level admin sub-role bypasses per-module checks.
    if (userRole?.key === 'admin') {
      return true
    }
    if (route === 'admin-portal-roles' || route === 'role-editor') {
      return false
    }
    return isEnabled(permissions, route)
  }

  // Per-child permission/role gating, preserved from the original flat menu.
  const isVisibleRoute = route =>
    route.showMenu &&
    authUser &&
    route.roles.includes(authUser.role) &&
    isEnabledRoute(route.key === 'organizations' ? 'organization' : route.key)

  const isDrawerCollapsed = !openDrawer && !isSmallScreen

  const visibleItems = group => group.items.filter(isVisibleRoute)

  const handleGroupClick = groupKey => {
    if (!openDrawer) {
      setDrawerOpen()
      if (openGroupKey !== groupKey) {
        toggleGroup(groupKey)
      }
      return
    }
    toggleGroup(groupKey)
  }

  const handleStandaloneClick = () => {
    if (!openDrawer) {
      setDrawerOpen()
    }
    if (isSmallScreen) {
      setDrawerClose()
    }
  }

  const renderChildLink = route => {
    const IconComponent = MENU_ICONS[route.icon] || IconDashboard

    return (
      <Grid item key={route.key}>
        <Link
          to={route.path}
          component={NavLink}
          className={classes.inactive}
          onClick={isSmallScreen ? setDrawerClose : undefined}
          style={({ isActive }) => activeLinkStyle(isActive)}
          underline="none"
        >
          <IconComponent />
          {openDrawer && (
            <Typography
              component="span"
              fontFamily="lexend"
              variant="body2"
              className={classes.linkText}
              style={{ color: 'inherit' }}
            >
              {trans(route.menuLabel || route.key)}
            </Typography>
          )}
        </Link>
      </Grid>
    )
  }

  const renderStandalone = group => {
    const route = visibleItems(group)[0]
    if (!route) return null
    const IconComponent = MENU_ICONS[route.icon] || IconDashboard

    return (
      <Grid item key={group.key}>
        <Link
          to={route.path}
          component={NavLink}
          className={classes.standaloneLink}
          onClick={handleStandaloneClick}
          style={({ isActive }) => (isActive ? standaloneActiveStyle : undefined)}
          underline="none"
          aria-label={isDrawerCollapsed ? trans(route.key) : undefined}
        >
          <IconComponent />
          {openDrawer && (
            <Typography
              component="span"
              fontFamily="lexend"
              variant="body2"
              className={classes.menuLabel}
            >
              {trans(route.key)}
            </Typography>
          )}
        </Link>
      </Grid>
    )
  }

  const renderGroup = group => {
    const items = visibleItems(group)
    // A group with no visible children must not render an empty accordion.
    if (items.length === 0) return null

    const GroupIcon = MENU_ICONS[group.icon] || IconDashboard
    const isOpen = group.key === openGroupKey
    const regionId = `menu-group-${group.key}`
    const headerId = `menu-header-${group.key}`

    return (
      <Grid
        container
        item
        key={group.key}
        direction="column"
        className={clsx({ [classes.groupMenuItem]: isDrawerCollapsed })}
      >
        <Grid item>
          <button
            type="button"
            id={headerId}
            className={clsx(classes.groupHeader, {
              [classes.groupHeaderActive]: group.key === activeGroupKey,
            })}
            aria-expanded={isOpen}
            aria-controls={regionId}
            aria-label={isDrawerCollapsed ? trans(group.key) : undefined}
            onClick={() => handleGroupClick(group.key)}
          >
            <GroupIcon />
            {openDrawer && (
              <>
                <Typography
                  component="span"
                  fontFamily="lexend"
                  variant="body2"
                  className={classes.groupHeaderLabel}
                >
                  {trans(group.key)}
                </Typography>
                <KeyboardArrowDownIcon
                  fontSize="small"
                  className={clsx(classes.chevron, { [classes.chevronOpen]: isOpen })}
                />
              </>
            )}
          </button>
        </Grid>
        <Grid item>
          <Collapse in={isOpen && openDrawer} appear unmountOnExit={false} mountOnEnter>
            <Grid
              container
              direction="column"
              spacing={2}
              role="region"
              id={regionId}
              aria-labelledby={headerId}
              className={classes.groupChildren}
            >
              {items.map(renderChildLink)}
            </Grid>
          </Collapse>
        </Grid>
      </Grid>
    )
  }

  const renderMenuGroup = group =>
    group.standalone ? renderStandalone(group) : renderGroup(group)

  return (
    <Drawer
      variant="permanent"
      className={clsx(
        classes.drawer,
        openDrawer ? classes.drawerOpen : classes.drawerClose
      )}
      classes={{ paper: openDrawer ? classes.drawerOpen : classes.drawerClose }}
      anchor={isSmallScreen ? 'bottom' : 'left'}
    >
      <div className={classes.topPart} style={!openDrawer ? { paddingLeft: 0, justifyContent: 'center' } : undefined}>
        <ColoredLogoSvg short={!openDrawer} height={64} />
      </div>

      <Grid
        container
        direction="column"
        spacing={1}
        className={clsx(classes.menuList, {
          [classes.menuListCollapsed]: isDrawerCollapsed,
        })}
        component="nav"
      >
        {menu.map(renderMenuGroup)}

        {!isSmallScreen && (
          <Grid item>
            <IconButton
              disableFocusRipple
              disableRipple
              className={classes.btnToggle}
              edge="start"
              color="inherit"
              onClick={openDrawer ? setDrawerClose : setDrawerOpen}
              style={{
                display: 'flex',
                alignItems: 'center',
                marginLeft: '11px',
                color: '#4e5978',
              }}
            >
              <IconMenuControl rotate={openDrawer} />
            </IconButton>
          </Grid>
        )}
      </Grid>
    </Drawer>
  )
}

AppMenu.propTypes = {
  setDrawerOpen: PropTypes.func.isRequired,
  openDrawer: PropTypes.bool.isRequired,
  setDrawerClose: PropTypes.func.isRequired,
  authUser: PropTypes.shape({
    role: PropTypes.string,
  }),
}

export default connect(AppMenu)
